/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { Fragment, useMemo, useRef, useEffect } from 'react';
import { animated, useTransition } from '@react-spring/web';
import {
  Box,
  Text,
  Link,
  useBreakpointIndex,
  useThemeUI,
} from '@tishman/components';
import * as Icons from '@tishman/icons';
import { useLocation } from '@reach/router';

import { usePageContext } from '../../utils';

import type { ReactElement } from 'react';
import type { LinkProps } from '@tishman/components';

export type LogoType =
  | 'Rockefeller Center'
  | 'Top of the Rock'
  | 'Top of the Rock Buy Tickets'
  | 'Observation Deck Buy Tickets'
  | 'Observation Deck'
  | 'Observation Deck Dark'
  | 'Rainbow Room'
  | 'The Rink'
  | 'The Rink By Chase'
  | 'Rockefeller Center Tour'
  | '620 Loft and Garden'
  | 'Newsstand studios'
  | 'VIP Experiences'
  | 'Hero Experience'
  | 'Weather Room'
  | 'The Beam'
  | 'Skylift';

export interface MenuBarLogoProps<LinkState = unknown> {
  logo: LogoType;
  pageName?: string | null;
  /**
   * The link to follow when the logo is clicked.
   */
  link: {
    /** The path the Logo links to. */
    to: string;
    /** The label for the link (will be a title). */
    label: string;
    /** Optional state to add to location state when the logo is clicked. */
    state?: LinkProps<LinkState>['state'];
  } | null;
}

const getKey = ({
  props: { logo, pageName },
}: ReactElement<MenuBarLogoProps>) =>
  `${logo}${pageName ? `:${pageName}` : ''}`;

const logoStyles = {
  transition: 'color 0.7s ease-in-out',
  width: '100%',
  height: 'auto',
};

function LogoSvg({
  logo,
  pageName,
}: Pick<MenuBarLogoProps, 'logo' | 'pageName'>) {
  const breakpointIndex = useBreakpointIndex();
  const isMobile = breakpointIndex < 2;
  const themeUI = useThemeUI();
  const { pathname } = useLocation();
  const isRainbowRoomPathname = pathname.includes('rainbow-room');

  // Don't render anything until breakpoint calculation is complete
  if (breakpointIndex === -1) return null;

  if (isRainbowRoomPathname) {
    logo = 'Rainbow Room';
    pageName = '';
  }

  switch (logo) {
    case 'Rockefeller Center': {
      if (pageName !== undefined) {
        return (
          <Fragment>
            <Icons.RockefellercenterLogo
              style={{
                color: themeUI.theme.colors.text,
                marginTop: isMobile ? 24 : 0,
                maxWidth: 238,
                minWidth: 150,
                ...logoStyles,
              }}
            />
            {pageName ? (
              <Text
                ml={['-0.4em', null, null, '-0.5em']}
                mt={['-0.8em', null, null, '-1em']}
                sx={pageName === 'Private Events' ? { color: 'text' } : {}}
                variant="rockCenterPageName"
              >
                {pageName}
              </Text>
            ) : null}
          </Fragment>
        );
      }
      return (
        <Icons.RockefellercenterLogoType
          style={{
            color: themeUI.theme.colors.text,
            maxWidth: 238,
            minWidth: 150,
            paddingTop: isMobile ? '10px' : 0,
            ...logoStyles,
          }}
        />
      );
    }
    case 'Observation Deck': {
      return (
        <Box
          sx={{
            marginTop: ['25px', '5px', '50px', '50px', '-24px'],
            maxWidth: [215, 350, null, 250, 450],
            minWidth: [215, 215, 215, 215, 450],
            height: [125, 125, null, 195, 'auto'],
          }}
        >
          <Icons.ObservationdeckLogo style={{ ...logoStyles }} />
        </Box>
      );
    }
    case 'Observation Deck Dark': {
      return (
        <Box
          sx={{
            marginTop: ['25px', '5px', '50px', '50px', '-24px'],
            maxWidth: [215, 350, null, 250, 450],
            minWidth: [215, 215, 215, 215, 450],
            height: [125, 125, null, 195, 'auto'],
          }}
        >
          <Icons.ObservationdeckLogoDark style={{ ...logoStyles }} />
        </Box>
      );
    }
    case 'Rainbow Room': {
      return (
        <Fragment>
          <Icons.RainbowroomLogo
            style={{
              color: themeUI.theme.colors.text,
              maxWidth: 150,
              minWidth: 110,
              ...logoStyles,
            }}
          />
          {pageName ? (
            <Text mt="1em" variant="rainbowRoomPageName">
              {pageName}
            </Text>
          ) : null}
        </Fragment>
      );
    }
    case 'The Rink': {
      return (
        <Icons.RinkLogo
          style={{
            color: themeUI.theme.colors.text,
            marginTop: themeUI.theme.space[2],
            maxWidth: 250,
            minWidth: 114,
            width: '100%',
            height: 'auto',
          }}
        />
      );
    }
    case 'The Rink By Chase': {
      return (
        <Box
          sx={{
            marginTop: ['40px', 0, '50px', '10px', null],
            maxWidth: [125, 230, 130, 200, 230],
            minWidth: [125, 215, 100, 125, 200],
          }}
        >
          <Icons.RinkChaseLogo
            style={{
              ...logoStyles,
              color: themeUI.theme.colors.text,
            }}
          />
        </Box>
      );
    }
    case 'Top of the Rock': {
      return (
        <Icons.TopoftherockLogo
          style={{ maxWidth: 275, minWidth: 175, ...logoStyles }}
        />
      );
    }
    case 'Top of the Rock Buy Tickets': {
      return (
        <Icons.TopoftherockLogo
          style={{ maxWidth: 171, minWidth: 132, ...logoStyles }}
        />
      );
    }
    case 'Observation Deck Buy Tickets': {
      return (
        <Icons.ObservationdeckLogo
          style={{ maxWidth: 350, minWidth: 175, ...logoStyles }}
        />
      );
    }
    case 'Rockefeller Center Tour': {
      return (
        <Icons.RockefellerCenterTourLogo
          style={{
            color: themeUI.theme.colors.text,
            maxWidth: 175,
            minWidth: 139,
            ...logoStyles,
          }}
        />
      );
    }
    case '620 Loft and Garden': {
      return (
        <Icons.LoftAndGardenLogo
          style={{ maxWidth: 155, minWidth: 130, ...logoStyles }}
        />
      );
    }
    case 'Newsstand studios': {
      return (
        <Icons.NewsstandStudiosLogo
          style={{
            maxWidth: 360,
            minWidth: isMobile ? 165 : 155,
            position: 'absolute',
            top: 0,
            ...logoStyles,
          }}
        />
      );
    }
    case 'VIP Experiences': {
      return (
        <Icons.VipExperiences
          style={{
            maxWidth: 135,
            minWidth: isMobile ? 100 : 135,
            ...logoStyles,
          }}
        />
      );
    }
    case 'Hero Experience': {
      return (
        <Icons.HeroExperienceLogo
          style={{
            ...logoStyles,
            color: 'white',
            height: isMobile ? 37 : 150,
            maxWidth: isMobile ? 46 : 184,
            minWidth: isMobile ? 100 : 135,
            marginTop: isMobile ? themeUI.theme.space[3] : 0,
          }}
        />
      );
    }
    case 'Weather Room': {
      return (
        <Icons.WeatherRoom
          style={{
            maxWidth: isMobile ? 280 : 150,
            minWidth: isMobile ? 120 : 0,
            ...logoStyles,
          }}
        />
      );
    }
    case 'The Beam': {
      return (
        <Icons.BeamLogo
          style={{
            color: themeUI.theme.colors.text,
            maxWidth: 200,
            minWidth: 150,
            ...logoStyles,
          }}
        />
      );
    }
    case 'Skylift': {
      return (
        <Icons.SkyliftLogo
          style={{
            maxWidth: 250,
            minWidth: 150,
            marginTop: themeUI.theme.space[isMobile ? 5 : 4],
            ...logoStyles,
          }}
        />
      );
    }
  }
}

const useMountedRef = () => {
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => void (mounted.current = false);
  }, []);
  return mounted;
};

export function MenuBarLogo(props: MenuBarLogoProps): JSX.Element {
  const { locale } = usePageContext();

  const mounted = useMountedRef();
  const logo = useMemo(
    () => <LogoSvg logo={props.logo} pageName={props.pageName} />,
    [props.logo, props.pageName],
  );

  const transition = useTransition(logo, {
    key: getKey,
    from: () => ({ opacity: mounted.current ? 0 : 1 }),
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  return props.link ? (
    <Link
      state={props.link.state}
      sx={{
        // height: 'calc(100% - 8px)',
        color: 'currentColor',
        mt: locale ? [3, 0] : 0,
        position: 'relative',
      }}
      title={props.link.label}
      to={props.link.to}
    >
      {transition((style, element) => (
        <animated.div
          style={{
            ...style,
            display: 'flex',
            position: 'absolute',
            top: 0,
            width: '100%',
            flexDirection: 'column',
            alignItems: 'center',
            color: 'logo',
          }}
        >
          {element}
        </animated.div>
      ))}
    </Link>
  ) : (
    <Box
      sx={{
        height: 'calc(100% - 8px)',
        mt: locale ? [3, 0] : 0,
        position: 'relative',
      }}
    >
      {transition((style, element) => (
        <animated.div
          style={{
            ...style,
            display: 'flex',
            position: 'absolute',
            top: 0,
            width: '100%',
            flexDirection: 'column',
            alignItems: 'center',
            color: 'logo',
          }}
        >
          {element}
        </animated.div>
      ))}
    </Box>
  );
}
